/**
 * ConfirmationModal Component
 * Generic confirmation dialog for destructive actions
 */

import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { AlertTriangle, Trash2, CheckCircle, Info, XCircle } from 'lucide-react';

/**
 * Icon configurations for different types
 */
const TYPE_CONFIG = {
  danger: {
    icon: Trash2,
    iconClassName: 'text-red-500',
    bgClassName: 'bg-red-100 dark:bg-red-900/30',
    actionClassName: 'bg-red-500 hover:bg-red-600 text-white',
  },
  warning: {
    icon: AlertTriangle,
    iconClassName: 'text-amber-500',
    bgClassName: 'bg-amber-100 dark:bg-amber-900/30',
    actionClassName: 'bg-amber-500 hover:bg-amber-600 text-white',
  },
  success: {
    icon: CheckCircle,
    iconClassName: 'text-green-500',
    bgClassName: 'bg-green-100 dark:bg-green-900/30',
    actionClassName: 'bg-green-500 hover:bg-green-600 text-white',
  },
  info: {
    icon: Info,
    iconClassName: 'text-blue-500',
    bgClassName: 'bg-blue-100 dark:bg-blue-900/30',
    actionClassName: 'bg-blue-500 hover:bg-blue-600 text-white',
  },
};

/**
 * ConfirmationModal component
 */
export function ConfirmationModal({
  open,
  onOpenChange,
  onConfirm,
  onCancel,
  title = 'Are you sure?',
  description = 'This action cannot be undone.',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'danger',
  isLoading = false,
  showIcon = true,
}) {
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.danger;
  const Icon = config.icon;

  const handleConfirm = () => {
    onConfirm?.();
  };

  const handleCancel = () => {
    onCancel?.();
    onOpenChange?.(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="confirmation-modal">
        <AlertDialogHeader>
          {showIcon && (
            <div className="flex justify-center mb-4">
              <div className={cn('w-12 h-12 rounded-full flex items-center justify-center', config.bgClassName)}>
                <Icon className={cn('h-6 w-6', config.iconClassName)} />
              </div>
            </div>
          )}
          <AlertDialogTitle className="text-center">{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-center">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="sm:justify-center gap-2">
          <AlertDialogCancel 
            onClick={handleCancel}
            disabled={isLoading}
            data-testid="confirmation-cancel-btn"
          >
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading}
            className={config.actionClassName}
            data-testid="confirmation-confirm-btn"
          >
            {isLoading ? 'Processing...' : confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Delete confirmation modal (shorthand)
 */
export function DeleteConfirmationModal({
  open,
  onOpenChange,
  onConfirm,
  itemName = 'item',
  itemType = 'item',
  isLoading = false,
}) {
  return (
    <ConfirmationModal
      open={open}
      onOpenChange={onOpenChange}
      onConfirm={onConfirm}
      type="danger"
      title={`Delete ${itemType}?`}
      description={`Are you sure you want to delete "${itemName}"? This action cannot be undone.`}
      confirmText="Delete"
      isLoading={isLoading}
    />
  );
}

/**
 * Submit for review confirmation modal
 */
export function SubmitReviewModal({
  open,
  onOpenChange,
  onConfirm,
  documentName,
  isLoading = false,
}) {
  return (
    <ConfirmationModal
      open={open}
      onOpenChange={onOpenChange}
      onConfirm={onConfirm}
      type="info"
      title="Submit for Review?"
      description={`Are you sure you want to submit "${documentName}" for review? You won't be able to edit it until the review is complete.`}
      confirmText="Submit"
      isLoading={isLoading}
    />
  );
}

/**
 * Approve document modal
 */
export function ApproveDocumentModal({
  open,
  onOpenChange,
  onConfirm,
  documentName,
  isLoading = false,
}) {
  return (
    <ConfirmationModal
      open={open}
      onOpenChange={onOpenChange}
      onConfirm={onConfirm}
      type="success"
      title="Approve Document?"
      description={`Are you sure you want to approve "${documentName}"? This will mark the document as final.`}
      confirmText="Approve"
      isLoading={isLoading}
    />
  );
}

/**
 * Reject document modal
 */
export function RejectDocumentModal({
  open,
  onOpenChange,
  onConfirm,
  documentName,
  isLoading = false,
}) {
  return (
    <ConfirmationModal
      open={open}
      onOpenChange={onOpenChange}
      onConfirm={onConfirm}
      type="danger"
      title="Reject Document?"
      description={`Are you sure you want to reject "${documentName}"? The author will need to make revisions.`}
      confirmText="Reject"
      isLoading={isLoading}
    />
  );
}

export default ConfirmationModal;
